import React, { useCallback, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Field, Form as FinalForm } from 'react-final-form'
import { toast } from 'react-toastify'
import {
  Button,
  Checkbox,
  Form,
  Grid,
  Header,
  Icon,
  Item,
  Message,
  Segment,
} from 'semantic-ui-react'

import { BatchLookupValue } from '../../../redux/types/ui'
import { AppState } from '../../../redux/store'
import { PRODUCT_TYPE_NAME_BY_ID } from '../../../consts'
import { InputWithSave } from '../InputWithSave'
import imgHolder from '../../../assets/imgHolder.png'
import {
  deleteBatchAliasAssignments,
  postProductAliases,
  setBatchAliasAssignmentsIsIgnored,
  updateBatchAliasWorkstreamTags,
} from '../../../redux/actions/productAliases'
import { batchListMoveDown, setBatchLookupValuesAlias } from '../../../redux/actions/ui'
import { BatchIgnoreBtn } from '../styles'

interface Props {
  batchLookupValues: BatchLookupValue[]
  isBatchAliasAssigned: boolean
}

const BatchAssignment = (props: Props) => {
  const dispatch = useDispatch()
  const { batchLookupValues, isBatchAliasAssigned } = props
  const { currentBatchId } = useSelector((state: AppState) => state.ui)
  const { batchAliasAssignments } = useSelector((state: AppState) => state.productAliases)

  const [autoConfirmed, setAutoConfirmed] = useState<(string | number)[]>([])

  const currentValue = currentBatchId ? batchLookupValues[parseInt(currentBatchId)] : undefined
  const currentAssignment = currentBatchId
    ? batchAliasAssignments[parseInt(currentBatchId)]
    : undefined

  const batchStoreName = currentValue?.store || ''
  const batchLookupName = currentValue?.name || '--'
  const batchLookupProductType = currentValue?.productType
    ? `, ${PRODUCT_TYPE_NAME_BY_ID[parseInt(currentValue.productType)]}`
    : ''
  const batchLookupAlias = currentValue?.alias || ''
  const batchSelectedProduct = currentAssignment?.batchSelectedProduct || null
  const batchSelectedProductAliases = currentAssignment?.batchSelectedProduct?.aliases || []
  const isConfirmed = currentAssignment?.isConfirmed
  const isIgnored = currentAssignment?.isIgnored
  const workstreamTags = currentAssignment?.workstreamTags

  const isInAliases =
    batchSelectedProductAliases.findIndex(
      (alias: string) => alias.toLowerCase() === batchLookupAlias.trim().toLowerCase()
    ) !== -1

  let formReset = useCallback((initialValues?: object) => {}, [])

  useEffect(() => {
    if (currentBatchId && formReset) {
      formReset()
    }
  }, [currentBatchId, formReset])

  const handleIgnoreClick = () => {
    if (!currentBatchId) {
      return
    }

    dispatch(
      setBatchAliasAssignmentsIsIgnored({
        batchId: currentBatchId,
        isIgnored: !currentAssignment?.isIgnored,
      })
    )

    if (!currentAssignment?.isIgnored) {
      dispatch(batchListMoveDown())
    }
  }

  const toggleWorkstreamTags = (tagKey: keyof NonNullable<typeof workstreamTags>) => {
    if (!currentBatchId) return
    const tagValue = workstreamTags ? !workstreamTags[tagKey] : true
    const toggledTags = { [tagKey]: tagValue }

    dispatch(
      updateBatchAliasWorkstreamTags({ batchId: currentBatchId, workstreamTags: toggledTags })
    )
  }

  const onClickConfirm = () => {
    if (currentBatchId && batchSelectedProduct?.id && !autoConfirmed.includes(currentBatchId)) {
      setAutoConfirmed([...autoConfirmed, currentBatchId])
      if (!batchLookupValues[parseInt(currentBatchId)].alias) {
        if (!toast.isActive('post-product-aliases-error')) {
          toast("Alias can't be empty", {
            containerId: 'error',
            toastId: 'post-product-aliases-error',
          })
        }
      } else {
        dispatch(
          postProductAliases({
            id: batchSelectedProduct.id.toString(),
            alias: batchLookupValues[parseInt(currentBatchId)].alias.trim(),
            batchId: currentBatchId,
            requestUuid: batchSelectedProduct.request_uuid,
          })
        )
        dispatch(batchListMoveDown())
      }
    }
  }

  return (
    <Segment basic style={{ minHeight: 179 }}>
      {currentBatchId ? (
        <>
          <Grid stackable>
            <Grid.Row>
              <Grid.Column width={12}>
                <Header as="h5" color="grey" style={{ marginBottom: 2, fontWeight: 'normal' }}>
                  {batchStoreName}
                </Header>
                <h2
                  style={{
                    marginBottom: 20,
                    marginTop: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  {` ${batchLookupName}${batchLookupProductType}`}
                </h2>
              </Grid.Column>
              <Grid.Column width={2} style={{ textAlign: 'right' }}>
                <BatchIgnoreBtn basic compact className="ignore-btn" onClick={handleIgnoreClick}>
                  {isConfirmed ? null : isIgnored ? '(Un-ignore)' : '(Ignore this)'}
                </BatchIgnoreBtn>
              </Grid.Column>
              <Grid.Column width={2}>
                {/* Using SemanticUI classes for layout not a real form */}
                <div className="ui form">
                  <Form.Field>
                    <label>Workstream Tags</label>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        paddingLeft: '0.1em',
                      }}
                    >
                      <Checkbox
                        label="Needs TLC"
                        onChange={() => {
                          toggleWorkstreamTags('needsTlc')
                        }}
                        checked={workstreamTags?.needsTlc}
                        disabled={isIgnored}
                      />
                      <Checkbox
                        label="Fix category"
                        onChange={() => {
                          toggleWorkstreamTags('fixCategory')
                        }}
                        checked={workstreamTags?.fixCategory}
                        disabled={isIgnored}
                      />
                      <Checkbox
                        label="No match"
                        onChange={() => {
                          toggleWorkstreamTags('noMatch')
                        }}
                        checked={workstreamTags?.noMatch}
                        disabled={isIgnored}
                      />
                    </div>
                  </Form.Field>
                </div>
              </Grid.Column>
            </Grid.Row>
            {isIgnored ? (
              <Grid.Row>
                <Grid.Column>
                  <Message style={{ marginTop: 40 }} content="Item ignored." />
                </Grid.Column>
              </Grid.Row>
            ) : (
              <Grid.Row>
                <Grid.Column width={7}>
                  <FinalForm
                    onSubmit={(values) => {
                      if (!values.alias || values.alias.trim().length === 0) {
                        if (!toast.isActive('post-product-aliases-error')) {
                          toast("Alias can't be empty", {
                            containerId: 'error',
                            toastId: 'post-product-aliases-error',
                          })
                        }
                        return
                      }

                      dispatch(
                        setBatchLookupValuesAlias({
                          batchId: currentBatchId,
                          alias: values.alias.trim(),
                        })
                      )
                    }}
                    initialValues={{ alias: batchLookupAlias }}
                    render={({ handleSubmit, form: { reset } }) => {
                      formReset = reset
                      return (
                        <Form onSubmit={handleSubmit} style={{ paddingTop: 15 }}>
                          <Field name="alias">
                            {(props) => (
                              <Form.Field inline style={{ display: 'flex' }}>
                                <InputWithSave
                                  disabled={isBatchAliasAssigned}
                                  aliases={batchSelectedProductAliases}
                                  confirm={onClickConfirm}
                                  inputProps={props.input}
                                  showIsInAliases={isInAliases && !props.meta.dirty}
                                  showBtn={props.meta.dirty || isInAliases}
                                  saveBtnColor="blue"
                                  saveIcon="check"
                                  reset={reset}
                                />
                                <Icon
                                  name="arrow right"
                                  size="large"
                                  color="grey"
                                  style={{ lineHeight: 1.6 }}
                                />
                              </Form.Field>
                            )}
                          </Field>
                        </Form>
                      )
                    }}
                  />
                </Grid.Column>
                <Grid.Column width={9}>
                  {currentBatchId && batchSelectedProduct ? (
                    <Item.Group>
                      <Item>
                        <img
                          className="batch-assignment-image"
                          src={
                            batchSelectedProduct.photos[0]?.urls.small ||
                            batchSelectedProduct.photos[0]?.urls.original ||
                            imgHolder
                          }
                          alt={batchSelectedProduct.attributes.name}
                        />
                        <Item.Content>
                          <Item.Header>{batchSelectedProduct.attributes.name}</Item.Header>
                          {isConfirmed ? null : (
                            <Button
                              size="mini"
                              compact
                              floated="right"
                              icon="cancel"
                              style={{ marginLeft: 5 }}
                              onClick={() => {
                                dispatch(
                                  deleteBatchAliasAssignments({
                                    batchId: currentBatchId,
                                  })
                                )
                              }}
                            />
                          )}
                          <Button
                            style={{
                              display: isConfirmed ? 'none' : 'inline-block',
                            }}
                            size="mini"
                            color="green"
                            compact
                            floated="right"
                            content="Confirm"
                            onClick={() => onClickConfirm()}
                          />
                          <Item.Meta>{batchSelectedProduct.attributes.brand}</Item.Meta>
                          <Item.Extra>
                            <a
                              href={batchSelectedProduct.attributes.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {batchSelectedProduct.id}
                            </a>
                            {` ${batchSelectedProduct.attributes.lineage} |
                    ${batchSelectedProduct.attributes.product_type} |
                    ${batchSelectedProduct.attributes.brand_subtype}`}
                          </Item.Extra>
                        </Item.Content>
                      </Item>
                    </Item.Group>
                  ) : (
                    <p style={{ paddingTop: 20 }}>Select a matched product.</p>
                  )}
                </Grid.Column>
              </Grid.Row>
            )}
          </Grid>
        </>
      ) : null}
    </Segment>
  )
}

export default BatchAssignment
